/**
 * Texte und Fachstatus der Rohrnetzansicht (TH-2 Stufe B) – rein, ohne React,
 * damit Seite und Test dieselben Regeln lesen (TX-STANDARD S1.5, S1.7).
 */
import type { Fachstatus } from '../../muster';
import { formatiereZahl } from '../../muster';
import { istSchranke, type Vollstaendigkeit } from './schranke';

/** Pa → kPa; Fehlstelle bleibt Fehlstelle (nie 0). */
export function kpa(pa: number | null | undefined): number | null {
  return pa === null || pa === undefined || !Number.isFinite(pa) ? null : pa / 1000;
}

/** W → kW; Fehlstelle bleibt Fehlstelle. */
export function kw(w: number | null | undefined): number | null {
  return w === null || w === undefined || !Number.isFinite(w) ? null : w / 1000;
}

/** Eine Förderhöhe als Text, mit „≥“, wenn sie aus einer Teilmenge der Wege stammt (F72). */
export function foerderhoeheText(pa: number | null | undefined, v: Vollstaendigkeit | undefined): string {
  const t = formatiereZahl(kpa(pa), 1);
  return istSchranke(v) && t !== '—' ? `≥ ${t}` : t;
}

/** Der Grund, warum ein Verbraucher keinen Weg hat – kurz, als sichtbarer Text. */
export function grundtext(grund: string): string {
  switch (grund) {
    case 'ohne_anschluss': return 'nicht angeschlossen';
    case 'ohne_seite': return 'ohne Vor-/Rücklauf';
    case 'ohne_systemgruppe': return 'ohne Systemgruppe';
    case 'gruppe_ohne_auslegung': return 'Gruppe ohne Auslegung';
    case 'netzluecke': return 'Netzlücke';
    case 'ohne_massenstrom': return 'ohne Massenstrom';
    default: return grund;
  }
}

/** Güte eines Wegs oder einer Gruppe (Ampel des Rechenkerns) → Fachstatus mit Text. */
export function zustandGuete(guete: string | null | undefined): Fachstatus {
  switch (guete) {
    case 'gruen': return { zustand: 'gut', text: 'im Rahmen' };
    case 'gelb': return { zustand: 'warnung', text: 'prüfen' };
    case 'rot': return { zustand: 'fehler', text: 'außerhalb' };
    default: return { zustand: 'neutral', text: 'ohne Bewertung' };
  }
}

/**
 * Herkunft eines Eingangswerts im Rechenweg → Fachstatus. `keine` ist ein
 * Fehler und kein Grau: Ein fehlender Beiwert macht die Summe zur unteren
 * Schranke.
 */
export function zustandRechenherkunft(wert: string | null | undefined): Fachstatus {
  if (!wert) return { zustand: 'neutral', text: '—' };
  if (wert === 'modell' || wert === 'datenblatt') return { zustand: 'gut', text: wert };
  if (wert === 'gerechnet' || wert === 'werkstoff' || wert === 'weg' || wert === 'kennlinie') {
    return { zustand: 'info', text: wert };
  }
  if (wert === 'keine') return { zustand: 'fehler', text: 'keine' };
  return { zustand: 'warnung', text: wert };
}

/** Bewertung der Heizfläche eines Verbrauchers → Fachstatus. */
export function zustandHeizflaeche(b: string | null | undefined): Fachstatus {
  if (!b) return { zustand: 'neutral', text: 'keine Heizfläche' };
  if (b === 'widerspruch' || b === 'unter') return { zustand: 'fehler', text: b };
  if (b === 'ueber' || b === 'ohne_kennwert' || b === 'ohne_heizlast') return { zustand: 'warnung', text: b.replace(/_/g, ' ') };
  return { zustand: 'neutral', text: b };
}

/**
 * F-TH-10: `teilstreckeDruckgefaellePaM` ist (Reibung + Einzelwiderstände) / Länge –
 * KEIN Rohrreibungsgefälle R. Die Beschriftung sagt das, damit niemand 673,6 „Pa/m“
 * neben R = 93,05 Pa/m als R liest.
 */
export const DRUCKGEFAELLE_TITEL = 'mittleres Druckgefälle (Reibung + Einzelwiderstände) / Länge';
